/* HOGGAR — conseiller taille (snippet hoggar-fitting) : 3 étapes + résultat. Calcul local : mensurations gardées sur l'appareil, jamais envoyées. Vanilla. */
const KEY = 'hg-fit-v1';

// Aisance visée au tour de poitrine (vêtement − corps, en cm) : [ajusté, comme prévu, ample] ; longueur idéale = ratio × taille.
// Réglée pour que la taille habituelle donne le tombé oversize prévu (homme 175 cm / 72 kg, morphologie moyenne → M).
const FITS = {
  'tshirt-boxy-210': { ease: [9, 15, 21], ratio: 0.417 },
  'sweat-boxy-345': { ease: [33, 37, 41], ratio: 0.411 },
  // T-shirt classique (29/10/2026) : coupe près du corps, tailles petites (homme 175 cm / 72 kg → L)
  'tshirt-classique-180': { ease: [4, 8, 12], ratio: 0.389 },
};
// Mensurations moyennes (cm) pour une taille et un IMC de référence : [valeur, + par point d'IMC, + par cm de taille]
const BODY = {
  m: { h: 175, bmi: 23.5, chest: [97, 2.3, 0.2], waist: [85, 3, 0.25], hips: [97, 2, 0.25] },
  f: { h: 165, bmi: 22, chest: [90, 2.2, 0.2], waist: [74, 2.6, 0.2], hips: [99, 2.4, 0.3] },
};
// Morphologie : plus fine / moyenne / plus forte (cm ajoutés au tour moyen)
const SHAPE = { chest: [-4, 0, 5], waist: [-5, 0, 7], hips: [-4, 0, 5] };
const ZONES = [['chest', 'Poitrine'], ['waist', 'Ventre'], ['hips', 'Hanches'], ['length', 'Longueur']];
const PREF_HINT = ['Plus près du corps que prévu.', 'Le tombé pour lequel le vêtement a été pensé.', 'Encore plus large que prévu.'];
const TOL = { chest: 5, waist: 5, hips: 5, length: 3 };
const DEFAULTS = { g: 'm', h: 175, w: 72, hu: 'cm', wu: 'kg', shape: { chest: 1, waist: 1, hips: 1 }, pref: 1 };

const load = () => {
  try {
    const s = JSON.parse(localStorage.getItem(KEY));
    if (s && s.h && s.w) return { ...DEFAULTS, ...s, shape: { ...DEFAULTS.shape, ...s.shape } };
  } catch (_) {}
  return null;
};
const save = (s) => { try { localStorage.setItem(KEY, JSON.stringify(s)); } catch (_) {} };
const clamp = (x, a, b) => Math.max(a, Math.min(b, x));

// Compteur (28/10/2026, demande utilisateur) : événements Shopify lus par le pixel personnalisé « HOGGAR — conseiller taille »
// (Paramètres > Événements clients), qui les envoie à Google Analytics si le visiteur accepte les statistiques.
// Seulement l'étape, la taille et le produit : jamais la taille en cm, le poids ni la morphologie.
const track = (name, data = {}) => {
  try { window.Shopify?.analytics?.publish?.(name, data); } catch (_) {}
};

// Tour de poitrine, ventre, hanches estimés ; avg = même personne en morphologie moyenne
const body = (s) => {
  const b = BODY[s.g] || BODY.m;
  const bmi = s.w / (s.h / 100) ** 2;
  const out = { bmi };
  ['chest', 'waist', 'hips'].forEach((k) => {
    const [v, perBmi, perCm] = b[k];
    out[k] = v + perBmi * (bmi - b.bmi) + perCm * (s.h - b.h) + SHAPE[k][s.shape[k]];
  });
  return out;
};

// Écart au tombé visé pour chaque zone (cm) : négatif = plus ajusté, positif = plus ample.
// Coupe droite : ventre et hanches ont la même largeur que la poitrine ; l'écart y est jugé par rapport à une morphologie moyenne.
const evaluate = (s, fit, row) => {
  const b = body(s);
  const chest = row.chest * 2 - b.chest - fit.ease[s.pref];
  const sc = SHAPE.chest[s.shape.chest];
  const d = {
    chest,
    waist: chest + sc - SHAPE.waist[s.shape.waist],
    hips: chest + sc - SHAPE.hips[s.shape.hips],
    length: row.length - fit.ratio * s.h,
  };
  const score = d.chest ** 2 + (d.waist < 0 ? 0.8 : 0.15) * d.waist ** 2 + (d.hips < 0 ? 0.5 : 0.1) * d.hips ** 2 + 0.15 * d.length ** 2;
  return { d, score };
};
const recommend = (s, fit, rows) => {
  const all = rows.map((row) => ({ row, ...evaluate(s, fit, row) }));
  return { all, best: all.reduce((a, b) => (b.score < a.score ? b : a)) };
};
const verdict = (k, v) => (Math.abs(v) <= TOL[k] ? 0 : v < 0 ? -1 : 1);

const fmtH = (cm, u) => {
  if (u === 'cm') return `${Math.round(cm)}<small>cm</small>`;
  const inch = Math.round(cm / 2.54);
  return `${Math.floor(inch / 12)}<small>ft</small> ${inch % 12}<small>in</small>`;
};
const fmtW = (kg, u) => (u === 'kg' ? `${Math.round(kg)}<small>kg</small>` : `${Math.round(kg * 2.20462)}<small>lb</small>`);

const init = (root) => {
  const dlg = root.querySelector('[data-hg-fitting]');
  if (!dlg || dlg.dataset.hgFitReady) return;
  dlg.dataset.hgFitReady = '1';
  let data;
  try { data = JSON.parse(dlg.querySelector('[data-hg-fit-json]').textContent); } catch (_) { return; }
  const fit = FITS[data.fiche];
  const rows = data.rows || [];
  if (!fit || !rows.length) return;

  const $ = (sel, el = dlg) => el.querySelector(sel);
  const $$ = (sel, el = dlg) => [...el.querySelectorAll(sel)];
  const sizeBox = root.querySelector('.hg-pdp__values--size');
  const sizeInputs = sizeBox ? [...sizeBox.querySelectorAll('input')] : [];
  const mainAdd = root.querySelector('[data-hg-add]');
  const json = root.querySelector('[data-hg-product-json]');
  let variants = [];
  try { variants = JSON.parse(json.textContent).variants || []; } catch (_) {}

  let s = load() || { ...DEFAULTS, shape: { ...DEFAULTS.shape } };
  let step = 1;
  let shown = null; // taille affichée dans le résultat (recommandée ou choisie par le visiteur)
  let rec = null;
  let unit = 'cm';
  const produit = ($('.hg-fit__prod strong')?.textContent || '').trim();

  /* ---------- Lien sous les tailles ---------- */
  const paintCta = () => {
    const done = root.querySelector('[data-hg-fit-done]');
    const open = root.querySelector('.hg-fit-cta__open');
    const stored = load();
    if (!done || !open) return;
    if (stored && stored.done) {
      done.querySelector('[data-hg-fit-done-size]').textContent = recommend(stored, fit, rows).best.row.size;
      done.hidden = false;
      open.hidden = true;
    } else {
      done.hidden = true;
      open.hidden = false;
    }
  };

  /* ---------- Étapes ---------- */
  const go = (n) => {
    step = n;
    $$('[data-hg-fit-step]').forEach((el) => (el.hidden = Number(el.dataset.hgFitStep) !== n));
    $$('[data-hg-fit-foot]').forEach((el) => (el.hidden = Number(el.dataset.hgFitFoot) !== n));
    $$('[data-hg-fit-bar]').forEach((b, i) => b.classList.toggle('is-on', i < n));
    $('[data-hg-fit-steplabel]').textContent = $(`[data-hg-fit-step="${n}"]`).dataset.label;
    $('.hg-fit__scroll').scrollTop = 0;
    if (n === 4) renderResult();
    paint();
  };

  const paint = () => {
    $$('[data-hg-fit-gender]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.hgFitGender === s.g)));
    $$('[data-hg-fit-hu]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.hgFitHu === s.hu)));
    $$('[data-hg-fit-wu]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.hgFitWu === s.wu)));
    $('[data-hg-fit-hval]').innerHTML = fmtH(s.h, s.hu);
    $('[data-hg-fit-wval]').innerHTML = fmtW(s.w, s.wu);
    const h = $('[data-hg-fit-h]'), w = $('[data-hg-fit-w]');
    h.value = s.h; w.value = s.w;
    h.setAttribute('aria-valuetext', $('[data-hg-fit-hval]').textContent);
    w.setAttribute('aria-valuetext', $('[data-hg-fit-wval]').textContent);
    [h, w].forEach((r) => r.style.setProperty('--p', `${((r.value - r.min) / (r.max - r.min)) * 100}%`));
    // Silhouette : plus large avec l'IMC, plus grande avec la taille
    const b = body(s);
    $('[data-hg-fit-figure] g').style.transform = `scale(${clamp(0.78 + (b.bmi - 18) * 0.035, 0.75, 1.3)}, ${clamp(s.h / 180, 0.8, 1.1)})`;
    $$('[data-hg-fit-shape]').forEach((el) => el.setAttribute('aria-pressed', String(Number(el.dataset.v) === s.shape[el.dataset.hgFitShape])));
    $$('[data-hg-fit-pref]').forEach((el) => el.setAttribute('aria-pressed', String(Number(el.dataset.hgFitPref) === s.pref)));
    $('[data-hg-fit-prefhint]').textContent = PREF_HINT[s.pref];
    // Estimation en direct (étape 1) : avec les réponses déjà données aux étapes suivantes
    $('[data-hg-fit-live]').textContent = recommend(s, fit, rows).best.row.size;
  };

  /* ---------- Résultat ---------- */
  const variantFor = (size) => {
    const cur = root.querySelector('[data-hg-variant-id]');
    const v0 = variants.find((v) => String(v.id) === (cur && cur.value));
    const idx = sizeBox ? [...root.querySelectorAll('[data-hg-option]')].indexOf(sizeBox.closest('[data-hg-option]')) : -1;
    if (!v0 || idx < 0) return null;
    return variants.find((v) => v.options.every((o, i) => (i === idx ? o === size : o === v0.options[i]))) || null;
  };

  const renderResult = () => {
    rec = recommend(s, fit, rows);
    if (!shown || !rows.some((r) => r.size === shown)) shown = rec.best.row.size;
    const best = rec.best;
    const cur = rec.all.find((x) => x.row.size === shown);
    const bestV = Object.fromEntries(ZONES.map(([k]) => [k, verdict(k, best.d[k])]));
    const allRight = Object.values(bestV).every((v) => v === 0);
    const sizes = rows.map((r) => r.size);
    const first = sizes[0], last = sizes[sizes.length - 1];

    // Phrase d'explication
    const aim = ['un tombé ajusté', 'le tombé prévu', 'un tombé ample'][s.pref];
    const to = s.pref === 1 ? 'au tombé prévu' : `à ${aim}`;
    const of = s.pref === 1 ? 'du tombé prévu' : `d’${aim}`;
    let why = allRight
      ? `Le ${best.row.size} correspond le mieux ${to}, sur tout le corps.`
      : `Le ${best.row.size} est la taille la plus proche ${of}.`;
    if (bestV.chest < 0 && best.row.size === last) why += ` Même en ${last}, la poitrine sera plus près du corps que voulu.`;
    else if (bestV.chest > 0 && best.row.size === first) why += ` Même en ${first}, ce sera plus large que voulu.`;
    else if (!allRight) {
      const z = ZONES.find(([k]) => bestV[k] !== 0);
      if (z) why += ` ${z[1]} : ${bestV[z[0]] < 0 ? (z[0] === 'length' ? 'un peu plus court' : 'un peu plus près du corps') : (z[0] === 'length' ? 'un peu plus long' : 'un peu plus ample')} que ${z[0] === 'length' ? 'prévu' : 'voulu'}.`;
    }

    const wanted = s.pref === 1 ? 'Juste pour la coupe prévue' : 'Juste comme tu l’aimes';
    const zoneText = (k, v) => {
      if (v === 0) return wanted;
      if (k === 'length') return v < 0 ? 'Un peu plus court que prévu' : 'Un peu plus long que prévu';
      return v < 0 ? 'Plus près du corps que voulu' : 'Plus ample que voulu';
    };
    const scaleWord = (k, v) => (v === 0 ? 'Juste' : k === 'length' ? (v < 0 ? 'Court' : 'Long') : v < 0 ? 'Ajusté' : 'Ample');
    const pos = (k, v) => 50 + clamp(v / (TOL[k] * 3), -1, 1) * 46;

    const fmt = (cm) => (unit === 'cm' ? String(cm).replace('.', ',') : (cm / 2.54).toFixed(1).replace('.', ','));
    const v = variantFor(shown);
    const avail = !v || v.available;

    $('[data-hg-fit-step="4"]').innerHTML = `
      <h2 class="hg-fit__title hg-fit__title--center">Ta taille</h2>
      <div class="hg-fit__ring"><b>${best.row.size}</b><small>${allRight ? 'Idéale' : 'Conseillée'}</small></div>
      <p class="hg-fit__why">${why}</p>
      <ul class="hg-fit__zones">
        ${ZONES.map(([k, label]) => `<li><span>${label}</span><span class="hg-fit__dot hg-fit__dot--${bestV[k] === 0 ? 'ok' : 'off'}">${zoneText(k, bestV[k])}</span></li>`).join('')}
      </ul>
      <p class="hg-fit__note">D’après ta taille, ton poids et ta morphologie, comparés au guide des tailles de ce produit. C’est un conseil, pas une garantie.</p>
      <div class="hg-fit__sizes" role="group" aria-label="Comparer une taille">
        ${sizes.map((z) => `<button type="button" data-hg-fit-size="${z}" aria-pressed="${z === shown}">${z === best.row.size ? '<i>Toi</i>' : ''}${z}</button>`).join('')}
      </div>
      <div class="hg-fit__bars">
        ${ZONES.map(([k, label]) => { const val = cur.d[k], vv = verdict(k, val); return `<div class="hg-fit__bar"><span>${label}</span><span class="hg-fit__track"><i style="left:${pos(k, val)}%"></i></span><span>${scaleWord(k, vv)}</span></div>`; }).join('')}
        <div class="hg-fit__bar hg-fit__bar--scale"><span></span><span class="hg-fit__scale"><span>Ajusté</span><span>Juste</span><span>Ample</span></span><span></span></div>
      </div>
      <details class="hg-fit__chart" open>
        <summary>Guide des tailles</summary>
        <div class="hg-fit__unit" role="group" aria-label="Unité"><button type="button" data-hg-fit-unit="cm" aria-pressed="${unit === 'cm'}">cm</button><button type="button" data-hg-fit-unit="in" aria-pressed="${unit === 'in'}">in</button></div>
        <table>
          <thead><tr><th scope="col">Taille</th><th scope="col">Longueur</th><th scope="col">Épaules</th><th scope="col">Poitrine</th><th scope="col">Manche</th></tr></thead>
          <tbody>${rows.map((r) => `<tr${r.size === shown ? ' class="is-on"' : ''}><th scope="row">${r.size}</th><td>${fmt(r.length)}</td><td>${fmt(r.shoulder)}</td><td>${fmt(r.chest)}</td><td>${fmt(r.sleeve)}</td></tr>`).join('')}</tbody>
        </table>
        <p class="hg-fit__note">Mesures du vêtement à plat, en ${unit === 'cm' ? 'centimètres' : 'pouces'}.</p>
      </details>`;

    const add = $('[data-hg-fit-add]');
    add.textContent = avail ? `Ajouter la taille ${shown} au panier` : `Taille ${shown} épuisée dans cette couleur`;
    add.disabled = !avail || !mainAdd;
    $('[data-hg-fit-select]').textContent = `Choisir ${shown} sur la page`;
  };

  const pickOnPage = (size) => {
    const input = sizeInputs.find((i) => i.value === size);
    if (!input) return false;
    if (!input.checked) input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  };

  /* ---------- Événements ---------- */
  const open = () => {
    const stored = load();
    if (stored) s = stored;
    shown = null;
    if (typeof dlg.showModal === 'function') dlg.showModal(); else dlg.setAttribute('open', '');
    document.documentElement.classList.add('hg-fit-open');
    go(stored && stored.done ? 4 : 1);
    track('hg_fit_ouvert', { produit, deja_fait: !!(stored && stored.done) });
  };
  const close = () => { if (dlg.open) dlg.close(); };
  dlg.addEventListener('close', () => document.documentElement.classList.remove('hg-fit-open'));
  root.addEventListener('click', (e) => { if (e.target.closest('[data-hg-fit-open]')) open(); });
  dlg.addEventListener('click', (e) => {
    const t = e.target;
    if (t === dlg || t.closest('[data-hg-fit-close]')) return close();
    const b = t.closest('button');
    if (!b) return;
    const d = b.dataset;
    if (d.hgFitGender) s.g = d.hgFitGender;
    else if (d.hgFitHu) s.hu = d.hgFitHu;
    else if (d.hgFitWu) s.wu = d.hgFitWu;
    else if (d.hgFitHstep) s.h = clamp(s.h + Number(d.hgFitHstep) * (s.hu === 'cm' ? 1 : 2.54), 140, 210);
    else if (d.hgFitWstep) s.w = clamp(s.w + Number(d.hgFitWstep) * (s.wu === 'kg' ? 1 : 0.45359), 40, 150);
    else if (d.hgFitShape) s.shape[d.hgFitShape] = Number(d.v);
    else if (d.hgFitPref) s.pref = Number(d.hgFitPref);
    else if ('hgFitNext' in d) {
      if (step === 3) { s.done = true; save(s); paintCta(); }
      go(step + 1);
      if (step === 4) track('hg_fit_resultat', { produit, taille: rec.best.row.size });
      return;
    } else if ('hgFitBack' in d) return go(step - 1);
    else if ('hgFitEdit' in d) return go(1);
    else if (d.hgFitSize) { shown = d.hgFitSize; return renderResult(); }
    else if (d.hgFitUnit) { unit = d.hgFitUnit; return renderResult(); }
    else if ('hgFitSelect' in d) { pickOnPage(shown); track('hg_fit_choix', { produit, taille: shown }); return close(); }
    else if ('hgFitAdd' in d) {
      if (!pickOnPage(shown) || !mainAdd || mainAdd.disabled) return;
      track('hg_fit_ajout', { produit, taille: shown, conseillee: shown === rec?.best.row.size });
      close();
      mainAdd.click();
      return;
    } else return;
    if (step < 4) save({ ...s, done: load()?.done || false });
    paint();
  });
  $('[data-hg-fit-h]').addEventListener('input', (e) => { s.h = Number(e.target.value); paint(); });
  $('[data-hg-fit-w]').addEventListener('input', (e) => { s.w = Number(e.target.value); paint(); });
  // Arrondi à l'unité affichée quand on lâche le curseur
  $('[data-hg-fit-h]').addEventListener('change', () => { s.h = s.hu === 'cm' ? Math.round(s.h) : Math.round(s.h / 2.54) * 2.54; save({ ...s, done: load()?.done || false }); paint(); });
  $('[data-hg-fit-w]').addEventListener('change', () => { s.w = s.wu === 'kg' ? Math.round(s.w) : Math.round(s.w * 2.20462) / 2.20462; save({ ...s, done: load()?.done || false }); paint(); });

  paintCta();

  /* ---------- Mise en avant de l'encart ---------- */
  const cta = root.querySelector('[data-hg-fit-cta]');
  if (cta) {
    // Pulsation de l'icône la première fois que l'encart arrive à l'écran
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(([e]) => {
        if (!e.isIntersecting) return;
        io.disconnect();
        setTimeout(() => cta.classList.add('is-pulse'), 600);
        track('hg_fit_vu', { produit });
      }, { threshold: 0.9 });
      io.observe(cta);
    }
    // « Choisir ma taille » sans taille choisie : l'encart clignote aussi (capture sur root : hoggar-product.js arrête la propagation sur le bouton)
    const sizeField = sizeBox && sizeBox.closest('fieldset');
    root.addEventListener('click', (e) => {
      if (!sizeField || !sizeField.classList.contains('is-unpicked') || !e.target.closest('[data-hg-add], [data-hg-sticky-add]')) return;
      cta.classList.remove('is-flash'); void cta.offsetWidth; cta.classList.add('is-flash');
    }, true);
  }
};

const boot = () => document.querySelectorAll('[data-hg-pdp]').forEach(init);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
document.addEventListener('shopify:section:load', boot);
